import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import {
  MdAutoAwesome, MdSend, MdRefresh, MdEventAvailable, MdFactCheck, MdGrading, MdPayments,
  MdGroups, MdMenuBook, MdShield, MdNorthWest,
} from 'react-icons/md';
import { useAuth } from '../auth';
import { useCoach } from '../help/Coach';
import { useNavigate } from '../navIntent';
import {
  useMishkahChat, ChatMessage, chatCost, formatSYP, SYP_PER_USD,
} from '../help/mishkahChat';
import { CAPABILITIES, SUGGESTIONS } from '../help/match';
import '../help/help.css';
import '../help/assistant.css';
import './mishkahAI.css';

const CAP_ICON = {
  attendance: MdEventAvailable,
  registers: MdFactCheck,
  'registers-t': MdFactCheck,
  marks: MdGrading,
  money: MdPayments,
  students: MdGroups,
  howto: MdMenuBook,
};

/**
 * «مشكاة الآلي» — «اسأل مشكاة» as a whole page: a wide conversation for
 * tables and charts, the question box always at hand, and at the side
 * what it can do, each example one click away. Same conversation as the
 * corner chat (help/mishkahChat.js). Opened from ⤢ next to the corner
 * button, or from the sidebar.
 */
export default function MishkahAI() {
  const { user, role } = useAuth();
  const { guides, nav, start } = useCoach();
  const navigate = useNavigate();
  const {
    chat, busy, send, choose, clear, byId,
  } = useMishkahChat({ user, guides, nav });
  const [text, setText] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const firstName = String(user?.name || '').trim().split(/\s+/)[0] || '';
  const caps = CAPABILITIES.filter((c) => c.roles.includes(role));

  useEffect(() => { inputRef.current?.focus(); }, []);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chat]);

  const ask = (q) => {
    send(q);
    setText('');
    if (inputRef.current) { inputRef.current.style.height = ''; inputRef.current.focus(); }
  };
  const handlers = {
    onRun: (id) => start(id),
    onRead: (id) => navigate('help', { guide: id }),
    onStudent: (studentId) => navigate('student-directory', { studentId }),
    onChoose: choose,
  };

  return (
    <div className="mk">
      <header className="mk-hero">
        <div className="mk-stars" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} style={{ '--i': i, top: `${(i * 37) % 100}%`, left: `${(i * 53 + 7) % 100}%` }} />
          ))}
        </div>
        <div className="mk-orb" aria-hidden="true"><MdAutoAwesome size={34} /></div>
        <div className="mk-hero-text">
          <span className="mk-kicker"><MdAutoAwesome size={13} /> ذكاء اصطناعي</span>
          <h1>مشكاة الآلي</h1>
          <p>اسأل بكلماتك عن الحضور والعلامات{role === 'admin' ? ' والأقساط' : ''} والطلاب، فيجيبك بالأرقام والجداول والمخططات، أو يدلّك على الطريق خطوة بخطوة.</p>
          <small className="mk-credit">من تطوير المهندس خالد حميدي</small>
        </div>
        {chatCost(chat).count > 0 && (
          <div className="mk-meter" title={`بسعر ثابت: 1$ = ${SYP_PER_USD} ل.س جديدة`}>
            <span>كلفة هذه المحادثة</span>
            <strong>{formatSYP(chatCost(chat).syp)}</strong>
            <small>{chatCost(chat).count} سؤال · كل سؤال يُحسب</small>
          </div>
        )}
        {chat.length > 0 && (
          <button type="button" className="mk-new" onClick={clear}><MdRefresh size={17} /> محادثة جديدة</button>
        )}
      </header>

      <div className="mk-grid">
        <main className="mk-chat">
          <div className="mk-list" ref={listRef}>
            {chat.length === 0 ? (
              <div className="mk-welcome">
                <div className="mk-welcome-orb" aria-hidden="true"><MdAutoAwesome size={30} /></div>
                <h2>أهلاً {firstName}، بماذا أساعدك اليوم؟</h2>
                <p>أنا «مشكاة الآلي». اكتب سؤالك كما تقوله لزميلك، وتابع عليه («وإيمتى سجّل؟»)، أو ابدأ بواحد من هذه:</p>
                <div className="mk-starters">
                  {(SUGGESTIONS[role] || []).map((s) => (
                    <button key={s} type="button" onClick={() => ask(s)}>
                      <span>{s}</span>
                      <MdNorthWest size={16} />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              chat.map((m) => <ChatMessage key={m.id} m={m} byId={byId} busy={busy} wide {...handlers} />)
            )}
          </div>

          <form className="mk-input" onSubmit={(e) => { e.preventDefault(); ask(text); }}>
            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = `${Math.min(t.scrollHeight, 160)}px`; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(text); } }}
              placeholder="اسأل مشكاة… مثلاً: مين أكثر الطلاب غياباً هالشهر بالسابع؟"
              rows={1}
              maxLength={600}
              aria-label="سؤالك"
              data-guide="mishkah-input"
            />
            <button type="submit" className="mk-send" disabled={!text.trim() || busy} aria-label="إرسال">
              {busy ? <span className="mk-spin" /> : <MdSend size={21} />}
            </button>
            <small className="mk-hint">Enter للإرسال · Shift + Enter لسطر جديد</small>
          </form>
        </main>

        <aside className="mk-side">
          <h2 className="mk-side-title">ماذا يستطيع مشكاة؟</h2>
          {caps.map((c) => {
            const Icon = CAP_ICON[c.key] || MdAutoAwesome;
            return (
              <section key={c.key} className={`mk-cap mk-tone-${c.tone}`}>
                <div className="mk-cap-head">
                  <span className="mk-cap-icon"><Icon size={18} /></span>
                  <strong>{c.title}</strong>
                </div>
                <div className="mk-cap-list">
                  {c.examples.map((q) => (
                    <button key={q} type="button" onClick={() => ask(q)} disabled={busy}>{q}</button>
                  ))}
                </div>
              </section>
            );
          })}
          <p className="mk-note">
            <MdShield size={16} />
            <span>يفهم مشكاة سؤالك فقط؛ أما أسماء الطلاب وعلاماتهم{role === 'admin' ? ' وأقساطهم' : ''} فلا تخرج من نظام المدرسة.</span>
          </p>
        </aside>
      </div>
    </div>
  );
}
